import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Switch, Select, Button, Spin, Space, Tooltip, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { CreateUnitCategoryCommand, UpdateUnitCategoryCommand, ProjectDto, UnitCategoryBasicInfoDto } from 'api/models';
import { UnitCategoriesApi, ProjectsApi } from 'api/api';
import openNotificationWithIcon from 'utility/notification';
import FeatherIcon from 'feather-icons-react';
import { MinusCircleOutlined } from '@ant-design/icons';
import { BasicFormWrapper, CustomSwitch } from 'container/styled';

interface Props {
  visible: boolean;
  editMode: boolean;
  category?: UnitCategoryBasicInfoDto | null;
  onSuccess: (values: CreateUnitCategoryCommand | UpdateUnitCategoryCommand) => Promise<void>;
  onCancel: () => void;
}

const categoriesApi = new UnitCategoriesApi();
const projectsApi = new ProjectsApi();

export const UnitCategoryForm: React.FC<Props> = ({ visible, editMode, category, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [projects, setProjects] = React.useState<Array<ProjectDto>>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [floorStats, setFloorStats] = React.useState<{ [key: number]: { reserved: number; sold: number } }>({});
  const [floorRange, setFloorRange] = useState({ lowest: -1, top: 1 });

  const getFloorDescription = (floorNumber: number): string => {
    if (floorNumber === 0) return 'Përdhesë';
    if (floorNumber <= -1) return `Bodrum (${Math.abs(floorNumber)})`;
    return `Kati i ${numberToAlbanian(floorNumber)}`;
  };

  const numberToAlbanian = (num: number): string => {
    const numbers = ['parë', 'dytë', 'tretë', 'katërt', 'pestë', 'gjashtë', 'shtatë', 'tetë', 'nëntë', 'dhjetë'];
    if (num > 0 && num <= 10) {
      return numbers[num - 1];
    }
    return `${num}të`;
  };

  useEffect(() => {
    if (category && editMode) {
      form.setFieldsValue({
        id: category.id,
        name: category.name,
        description: category.description,
        hasFloors: category.hasFloors,
        projectId: category.projectId,
        isActive: category.isActive,
      });

      const fetchFloors = async () => {
        try {
          const response = await categoriesApi.apiUnitCategoriesIdFloorsGet({ id: category.id! });
          const floorsWithStats = response.data.reduce(
            (acc, floor) => ({
              ...acc,
              [floor.floorNumber!]: { reserved: floor.reservedUnitsCount || 0, sold: floor.soldUnitsCount || 0 },
            }),
            {},
          );

          setFloorStats(floorsWithStats as { [key: number]: { reserved: number; sold: number } });
          const floors = response.data.map(floor => ({
            id: floor.id,
            floorNumber: floor.floorNumber,
            description: floor.description,
          }));
          form.setFieldValue('floors', floors);

          const floorNumbers = floors.map(f => f.floorNumber).filter((n): n is number => n !== undefined);
          if (floorNumbers.length > 0) {
            setFloorRange({
              lowest: Math.min(...floorNumbers),
              top: Math.max(...floorNumbers),
            });
          }
        } catch (error) {
          openNotificationWithIcon('error', t('categories:floors-fetch-error', 'Failed to fetch floors'));
        }
      };

      fetchFloors();
    } else {
      form.setFieldsValue({
        isActive: true,
        hasFloors: true,
        floors: [],
      });
    }
  }, [category, editMode, form, t]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1 });
      setProjects((response.data.items as ProjectDto[]) || []);
    } catch (error) {
      openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch projects'));
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleFloorRangeChange = () => {
    const { lowest, top } = floorRange;
    if (lowest > top) {
      openNotificationWithIcon(
        'error',
        t('categories:invalid-range', 'Lowest floor must be less than or equal to top floor'),
      );
      return;
    }

    const floors = [];
    for (let i = lowest; i <= top; i++) {
      const existingFloor = form.getFieldValue('floors')?.find((f: any) => f.floorNumber === i);
      floors.push({
        floorNumber: i,
        description: existingFloor?.description || getFloorDescription(i),
        id: existingFloor?.id,
      });
    }
    form.setFieldValue('floors', floors);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (editMode) {
        const updateData: UpdateUnitCategoryCommand = {
          id: values.id,
          name: values.name,
          description: values.description,
          isActive: values.isActive,
          floors:
            values.floors?.map((floor: any) => ({
              id: floor.id || undefined,
              floorNumber: floor.floorNumber,
              description: floor.description,
              isActive: true,
            })) || [],
        };

        await categoriesApi.apiUnitCategoriesIdPut({
          id: values.id,
          updateUnitCategoryCommand: updateData,
        });
      } else {
        const createData: CreateUnitCategoryCommand = {
          projectId: values.projectId,
          name: values.name,
          description: values.description,
          floors: values.hasFloors
            ? values.floors?.map((floor: any) => ({
                floorNumber: floor.floorNumber,
                description: floor.description,
              })) || []
            : [],
        };

        await categoriesApi.apiUnitCategoriesPost({
          createUnitCategoryCommand: createData,
        });
      }

      openNotificationWithIcon(
        'success',
        t(
          `categories:${editMode ? 'updated' : 'created'}`,
          `Category ${editMode ? 'updated' : 'created'} successfully`,
        ),
      );
      await onSuccess(values);
    } catch (err) {
      openNotificationWithIcon('error', t('categories:error', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const validateFloorNumbers = (_: any, floors: any[]) => {
    if (!floors || floors.length === 0) {
      return Promise.reject(new Error(t('categories:floors-required', 'At least one floor is required')));
    }

    const floorNumbers = floors.map(f => f.floorNumber);
    const uniqueFloorNumbers = new Set(floorNumbers);
    if (floorNumbers.length !== uniqueFloorNumbers.size) {
      return Promise.reject(new Error(t('categories:duplicate-floors', 'Floor numbers must be unique')));
    }

    return Promise.resolve();
  };

  // Handle input change for lowest floor
  const handleLowestFloorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty input (will be treated as 0 later)
    if (inputValue === '') {
      setFloorRange(prev => ({ ...prev, lowest: 0 }));
      return;
    }

    // Parse the input value to integer
    const value = parseInt(inputValue);

    // Update state only if it's a valid number
    if (!isNaN(value)) {
      setFloorRange(prev => ({ ...prev, lowest: value }));
    }
  };

  // Handle input change for top floor
  const handleTopFloorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty input (will be treated as 0 later)
    if (inputValue === '') {
      setFloorRange(prev => ({ ...prev, top: 0 }));
      return;
    }

    // Parse the input value to integer
    const value = parseInt(inputValue);

    // Update state only if it's a valid number
    if (!isNaN(value)) {
      setFloorRange(prev => ({ ...prev, top: value }));
    }
  };

  return (
    <Modal
      title={t(`categories:${editMode ? 'edit' : 'create'}`, `${editMode ? 'Edit' : 'Create'} Category`)}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <BasicFormWrapper>
        {projectsLoading ? (
          <Spin size="large" />
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isActive: true }}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>

            {!editMode && (
              <Form.Item
                label={t('categories:project', 'Project')}
                name="projectId"
                rules={[{ required: true, message: t('categories:project-required', 'Project is required') }]}
              >
                <Select loading={projectsLoading} placeholder={t('categories:select-project', 'Select a project')}>
                  {projects.map(project => (
                    <Select.Option key={project.id} value={project.id}>
                      {project.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              label={t('categories:name', 'Category Name')}
              name="name"
              rules={[
                { required: true, message: t('categories:name-required', 'Category name is required') },
                { max: 200, message: t('categories:name-max', 'Category name cannot exceed 200 characters') },
              ]}
            >
              <Input prefix={<FeatherIcon icon="box" size={16} />} />
            </Form.Item>

            <Form.Item
              label={t('categories:description', 'Description')}
              name="description"
              rules={[
                { max: 500, message: t('categories:description-max', 'Description cannot exceed 500 characters') },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>

            {!editMode && (
              <Form.Item name="hasFloors" valuePropName="checked" initialValue={true}>
                <Checkbox
                  onChange={e => {
                    if (!e.target.checked) {
                      form.setFieldValue('floors', []);
                      setFloorRange({ lowest: -1, top: 1 });
                    } else {
                      handleFloorRangeChange();
                    }
                  }}
                >
                  {t('categories:has-floors', 'Category has floors')}
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item noStyle dependencies={['hasFloors']}>
              {({ getFieldValue }) =>
                getFieldValue('hasFloors') && (
                  <>
                    <Form.Item label={t('categories:floor-range', 'Floor Range')}>
                      <Space>
                        <Input
                          type="number"
                          value={floorRange.lowest}
                          onChange={handleLowestFloorChange}
                          placeholder={t('categories:lowest-floor', 'Lowest floor')}
                          style={{ width: 120 }}
                        />
                        <Input
                          type="number"
                          value={floorRange.top}
                          onChange={handleTopFloorChange}
                          placeholder={t('categories:top-floor', 'Top floor')}
                          style={{ width: 120 }}
                        />
                        <Button type="primary" onClick={handleFloorRangeChange}>
                          {t('categories:apply-range', 'Apply Range')}
                        </Button>
                      </Space>
                    </Form.Item>

                    <Form.List name="floors" rules={[{ validator: validateFloorNumbers }]}>
                      {(fields, { remove }) => {
                        const sortedFields = [...fields].sort((a, b) => {
                          const aFloorNum = getFieldValue(['floors', a.name, 'floorNumber']) || 0;
                          const bFloorNum = getFieldValue(['floors', b.name, 'floorNumber']) || 0;
                          return aFloorNum - bFloorNum;
                        });

                        return (
                          <div style={{ marginBottom: 24, display: 'flex', gap: '24px' }}>
                            <div style={{ flex: 1 }}>
                              <Form.Item label={t('categories:floors', 'Floors')}>
                                {sortedFields.map(({ key, name, ...restField }) => {
                                  const floorNumber = getFieldValue(['floors', name, 'floorNumber']);
                                  const stats = floorStats[floorNumber];
                                  const canDelete = !editMode || !stats || (stats.reserved === 0 && stats.sold === 0);

                                  return (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'floorNumber']}
                                        rules={[
                                          {
                                            required: true,
                                            message: t('categories:floor-number-required', 'Floor number is required'),
                                          },
                                        ]}
                                      >
                                        <Input
                                          type="number"
                                          placeholder={t('categories:floor-number', 'Floor number')}
                                          style={{ width: 120 }}
                                          disabled
                                        />
                                      </Form.Item>
                                      <Form.Item {...restField} name={[name, 'description']}>
                                        <Input
                                          placeholder={t('categories:floor-description', 'Floor description')}
                                          style={{ width: 300 }}
                                        />
                                      </Form.Item>
                                      {fields.length > 1 &&
                                        (canDelete ? (
                                          <MinusCircleOutlined onClick={() => remove(name)} />
                                        ) : (
                                          <Tooltip
                                            title={t('categories:floor-in-use', 'Floor has reserved or sold units')}
                                          >
                                            <MinusCircleOutlined
                                              style={{ color: '#00000040', cursor: 'not-allowed' }}
                                            />
                                          </Tooltip>
                                        ))}
                                    </Space>
                                  );
                                })}
                              </Form.Item>
                            </div>

                            <div
                              style={{
                                width: '200px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '8px',
                                padding: '16px',
                                background: '#fafafa',
                                position: 'relative',
                              }}
                            >
                              <div style={{ marginBottom: '16px', fontWeight: 500 }}>
                                {t('categories:floor-preview', 'Building Preview')}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column-reverse',
                                  gap: '2px',
                                  position: 'relative',
                                  perspective: '1000px',
                                }}
                              >
                                {sortedFields.map(({ name }, index) => {
                                  const floorNumber = getFieldValue(['floors', name, 'floorNumber']);
                                  const description = getFieldValue(['floors', name, 'description']);
                                  const isFirst = index === 0;

                                  return (
                                    <div
                                      key={name}
                                      style={{
                                        padding: '8px 12px',
                                        background: '#fff',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        transform: 'rotateX(5deg)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        position: 'relative',
                                        zIndex: sortedFields.length - index,
                                        marginBottom: isFirst ? '4px' : '0',
                                        borderBottom: isFirst ? '2px solid #d9d9d9' : '1px solid #d9d9d9',
                                        transition: 'all 0.2s ease',
                                      }}
                                      onMouseEnter={e => {
                                        e.currentTarget.style.transform = 'rotateX(5deg) translateX(4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                      }}
                                      onMouseLeave={e => {
                                        e.currentTarget.style.transform = 'rotateX(5deg)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                      }}
                                    >
                                      <div style={{ fontWeight: 500 }}>Floor {floorNumber}</div>
                                      {description && (
                                        <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                                          {description}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                <div
                                  style={{
                                    height: '4px',
                                    background: '#d9d9d9',
                                    borderRadius: '4px',
                                    marginTop: '2px',
                                    transform: 'rotateX(5deg)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </Form.List>
                  </>
                )
              }
            </Form.Item>

            {editMode && (
              <Form.Item label={t('categories:status', 'Status')} name="isActive" valuePropName="checked">
                <CustomSwitch />
              </Form.Item>
            )}

            <div className="form-actions">
              <Button onClick={onCancel} style={{ marginRight: 8 }}>
                {t('global:cancel', 'Cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editMode ? t('global:update', 'Update') : t('global:create', 'Create')}
              </Button>
            </div>
          </Form>
        )}
      </BasicFormWrapper>
    </Modal>
  );
};
